import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';

const FORM_URL = 'https://readdy.ai/api/form/da36h810938upkd4h5s0';

const countries = [
  'South Korea',
  'China',
  'Japan',
  'United States',
  'United Kingdom',
  'France',
  'Germany',
  'Vietnam',
  'Thailand',
  'Indonesia',
  'India',
  'Singapore',
  'Malaysia',
  'Philippines',
  'United Arab Emirates',
  'Saudi Arabia',
  'Other',
];

export default function ContactForm() {
  const { t } = useTranslation('contact');
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
  const [errorMsg, setErrorMsg] = useState('');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    // Honeypot check
    const honeypot = (formData.get('website_alt') as string) || '';
    if (honeypot.trim() !== '') {
      setStatus('success');
      form.reset();
      return;
    }

    setStatus('sending');
    setErrorMsg('');

    const params = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key !== 'website_alt' && value !== '') {
        params.append(key, String(value));
      }
    });

    try {
      const response = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } = {};
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = {};
      }

      const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || responseText;

      if (response.ok && parsed?.code === 'OK') {
        setStatus('success');
        form.reset();
      } else {
        setStatus('error');
        setErrorMsg(serverMsg || t('form.error.retry'));
      }
    } catch {
      setStatus('error');
      setErrorMsg(t('form.error.retry'));
    }
  };

  const inputClass =
    'w-full px-4 py-3 bg-white border border-cream-200 rounded-md text-sm text-ink-800 placeholder-ink-300 outline-none focus:border-warm-400 focus:ring-1 focus:ring-warm-300 transition-colors';
  const labelClass = 'block text-xs font-semibold text-ink-600 mb-1.5';

  return (
    <section className="w-full bg-white py-14 md:py-20">
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8 md:mb-10">
            <span className="inline-block text-xs font-semibold text-warm-500 uppercase tracking-[0.2em] mb-3">
              {t('form.label')}
            </span>
            <h2 className="text-2xl md:text-3xl lg:text-4xl font-bold text-ink-800 mb-3">
              {t('form.title')}
            </h2>
            <p className="text-sm md:text-base text-ink-500 max-w-xl mx-auto leading-relaxed">
              {t('form.subtitle')}
            </p>
          </div>

          <form
            data-readdy-form
            onSubmit={handleSubmit}
            className="bg-cream-50 rounded-2xl border border-cream-200 p-6 md:p-10"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
              <div>
                <label htmlFor="contact-name" className={labelClass}>
                  {t('form.name')}
                </label>
                <input
                  id="contact-name"
                  name="name"
                  type="text"
                  required
                  placeholder={t('form.name.placeholder')}
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="contact-email" className={labelClass}>
                  {t('form.email')}
                </label>
                <input
                  id="contact-email"
                  name="email"
                  type="email"
                  required
                  placeholder={t('form.email.placeholder')}
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="contact-company" className={labelClass}>
                  {t('form.company')}
                </label>
                <input
                  id="contact-company"
                  name="company"
                  type="text"
                  placeholder={t('form.company.placeholder')}
                  className={inputClass}
                />
              </div>
              <div>
                <label htmlFor="contact-country" className={labelClass}>
                  {t('form.country')}
                </label>
                <select id="contact-country" name="country" className={inputClass} defaultValue="">
                  <option value="" disabled>
                    {t('form.country.placeholder')}
                  </option>
                  {countries.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="contact-type" className={labelClass}>
                  {t('form.type')}
                </label>
                <select id="contact-type" name="inquiry_type" className={inputClass} defaultValue="general">
                  <option value="general">{t('form.type.general')}</option>
                  <option value="quote">{t('form.type.quote')}</option>
                  <option value="sample">{t('form.type.sample')}</option>
                  <option value="partnership">{t('form.type.partnership')}</option>
                  <option value="other">{t('form.type.other')}</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="contact-message" className={labelClass}>
                  {t('form.message')}
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  rows={5}
                  maxLength={500}
                  placeholder={t('form.message.placeholder')}
                  onChange={(e) => setCharCount(e.target.value.length)}
                  className={`${inputClass} resize-none`}
                />
                <p className="text-right text-[11px] text-ink-300 mt-1">{charCount} / 500</p>
              </div>
            </div>

            {/* Honeypot */}
            <input
              type="text"
              name="website_alt"
              className="honeypot-hidden"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
            />

            {status === 'error' && (
              <div className="mt-5 bg-red-50 border border-red-200 rounded-md px-4 py-3">
                <p className="text-sm text-red-700 font-medium">{t('form.error.title')}</p>
                <p className="text-xs text-red-600 mt-0.5">{errorMsg || t('form.error.retry')}</p>
              </div>
            )}

            {status === 'success' && (
              <div className="mt-5 bg-green-50 border border-green-200 rounded-md px-4 py-3">
                <p className="text-sm text-green-700 font-medium">{t('form.success.title')}</p>
                <p className="text-xs text-green-600 mt-0.5">{t('form.success.desc')}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={status === 'sending'}
              className="mt-6 w-full sm:w-auto px-8 py-3.5 bg-warm-500 text-white text-sm font-semibold rounded-md hover:bg-warm-600 disabled:opacity-60 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
            >
              {status === 'sending' ? t('form.sending') : t('form.submit')}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}